import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import MediaCard from "@/components/feature/MediaCard";
import { topicList } from "@/mocks/topics";
import { getMediaById, getTopicById } from "@/lib/content";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 6;

export default function TopicPage() {
  const { id } = useParams<{ id: string }>();
  const topic = id ? getTopicById(id) : undefined;
  const [visible, setVisible] = useState(PAGE_SIZE);

  usePageMeta({
    title: topic
      ? `${topic.title} - 专题合集 - po18`
      : "专题合集 - po18",
    description: topic ? topic.description.slice(0, 90) : "po18 精选专题合集。",
    keywords: topic ? `po18,${topic.title},专题合集,影视推荐` : "po18,专题合集",
  });

  if (!topic) {
    return (
      <SiteLayout>
        <div className="w-full px-4 md:px-6 py-24 text-center">
          <span className="w-16 h-16 mx-auto rounded-full bg-background-100 text-foreground-400 flex items-center justify-center text-3xl">
            <i className="ri-bookmark-3-line" />
          </span>
          <h1 className="mt-5 text-xl font-bold text-foreground-900">
            未找到该专题
          </h1>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-line text-lg" />
            返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const works = topic.mediaIds
    .map((mid) => getMediaById(mid))
    .filter((m): m is NonNullable<typeof m> => Boolean(m));

  const shown = works.slice(0, visible);
  const relatedTopics = topicList.filter((t) => t.id !== topic.id);

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-6">
        <nav className="pt-6 flex items-center gap-1.5 text-sm text-foreground-500">
          <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-800 font-medium">专题合集</span>
        </nav>

        {/* Hero */}
        <header className="mt-4 relative rounded-lg overflow-hidden">
          <div className="w-full h-[220px] md:h-[340px] bg-background-200">
            <img
              src={topic.image}
              alt={`${topic.title} 专题主图`}
              title={`${topic.title} - po18 精选专题`}
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/40 to-foreground-950/10" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-500 text-background-50 text-xs font-semibold">
              <i className="ri-bookmark-3-line" />
              精选专题
            </span>
            <h1 className="mt-3 text-2xl md:text-4xl font-black text-background-50">
              {topic.title}
            </h1>
            <p className="mt-2 text-sm md:text-base text-background-100/90">
              {topic.subtitle}
            </p>
          </div>
        </header>

        {/* 导语 */}
        <section className="mt-6 rounded-lg bg-background-100 border border-background-200/70 p-5 md:p-7">
          <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
            <span className="w-7 h-7 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center">
              <i className="ri-article-line" />
            </span>
            专题导语
          </h2>
          <p className="mt-4 text-base text-foreground-700 leading-loose">
            {topic.description}
          </p>
        </section>

        {/* 作品网格 */}
        <section className="mt-8">
          <div className="flex items-end justify-between gap-4 mb-5">
            <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
              <span className="w-7 h-7 rounded-md bg-accent-100 text-accent-600 flex items-center justify-center">
                <i className="ri-layout-grid-line" />
              </span>
              专题作品（{works.length}）
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {shown.map((item) => (
              <MediaCard key={item.id} item={item} />
            ))}
          </div>
          {visible < works.length && (
            <div className="mt-8 text-center">
              <button
                type="button"
                onClick={() => setVisible((v) => v + PAGE_SIZE)}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-secondary-100 text-secondary-900 text-sm font-semibold hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line text-lg" />
                加载更多作品
              </button>
            </div>
          )}
        </section>

        {/* 推荐理由 */}
        <section className="mt-8 rounded-lg bg-gradient-to-r from-primary-50 to-background-100 border border-primary-100 p-5 md:p-7">
          <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
            <span className="w-7 h-7 rounded-md bg-primary-500 text-background-50 flex items-center justify-center">
              <i className="ri-lightbulb-line" />
            </span>
            推荐理由
          </h2>
          <p className="mt-4 text-base text-foreground-700 leading-loose">
            {topic.reason}
          </p>
        </section>

        {/* 相关专题 */}
        <section className="mt-8">
          <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950 mb-5">
            <span className="w-7 h-7 rounded-md bg-secondary-100 text-secondary-700 flex items-center justify-center">
              <i className="ri-links-line" />
            </span>
            相关专题
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {relatedTopics.map((t) => (
              <Link
                key={t.id}
                to={`/topic/${t.id}`}
                className="group flex gap-4 cursor-pointer rounded-lg bg-background-100 border border-background-200/70 p-4 hover:border-primary-200 transition-colors"
              >
                <div className="w-32 sm:w-40 shrink-0 aspect-[16/9] rounded-md overflow-hidden bg-background-200">
                  <img
                    src={t.image}
                    alt={`${t.title} 专题`}
                    title={`${t.title} - po18 精选专题`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="min-w-0">
                  <h3 className="text-base font-bold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-1">
                    {t.title}
                  </h3>
                  <p className="mt-1 text-sm text-foreground-600 leading-relaxed line-clamp-2">
                    {t.subtitle}
                  </p>
                  <span className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-primary-600">
                    查看专题
                    <i className="ri-arrow-right-line" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}